'use client';

import { Info } from 'lucide-react';
import type { PhotoVariant } from './photo-flow-ui';

interface FacialRecognitionNoticeProps {
  /**
   * Where someone who wants to be removed should go. Passed in rather than
   * built from an album token, because the event's master link has no album:
   * one link now covers the whole race.
   */
  removalHref: string;
  selfRedactionEnabled: boolean;
  /** 'page' sets it in the public page's column; the look is the same. */
  variant?: PhotoVariant;
}

/**
 * Transparency notice for everyone who appears in the event photos, not just the
 * person searching. Rekognition indexes every face in every uploaded photo, so
 * people in the background have biometric data processed too. GDPR requires we
 * tell them this is happening, why, and how to object or be removed. Always
 * visible on the public album page; links to the self-serve removal flow when
 * the organiser has enabled it.
 */
export function FacialRecognitionNotice({ removalHref, selfRedactionEnabled, variant = 'page' }: FacialRecognitionNoticeProps) {
  const link = { color: 'var(--fvd-teal-text)', textDecoration: 'none', fontWeight: 500 };
  const note = (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--fvd-hairline-light)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <Info size={14} aria-hidden="true" style={{ color: 'var(--fvd-ink-muted)', flex: '0 0 auto', marginTop: 3 }} />
      <p style={{ margin: 0, fontSize: 12, lineHeight: 1.6, color: 'var(--fvd-ink-muted)', textAlign: 'left' }}>
        Photos from this event are processed with facial recognition so people can find their
        own pictures. Face data is used only to match photos and is deleted once the photos are
        no longer available (and within 12 months at the latest).{' '}
        {selfRedactionEnabled ? (
          <>
            If you appear in a photo and want it removed or your face blurred,{' '}
            <a href={removalHref} style={link}>remove yourself here</a>.{' '}
          </>
        ) : (
          <>To have a photo removed or your face blurred, contact the event organiser.{' '}</>
        )}
        See our{' '}
        <a href="/privacy" style={link}>privacy notice</a>{' '}
        for more.
      </p>
    </div>
  );
  // On a public page it closes the same centred column as the flow above it.
  return variant === 'hub' ? note : <div style={{ maxWidth: 560, margin: '0 auto', padding: '8px 16px 24px', boxSizing: 'border-box' }}>{note}</div>;
}
